import { useRef, useState } from 'react';
import { apiPost } from '@lib/apiClient';
import { ConfirmationCard, type ReadAs } from '@components/ConfirmationCard';
import { PhotoCapture } from '@components/PhotoCapture';
import { VoiceNote } from '@components/VoiceNote';
import { NearMissDialog } from '@components/NearMissDialog';
import { OverrideHatch } from '@components/OverrideHatch';
import type { Eligibility, IntakeConfig, MemberKit, NearMiss } from '@/types/intake';

/**
 * Bench "add to batch" card. Identify is already done (scan/type -> kit-lookup); here the user
 * verifies the member, captures the up-front consent-card (or box/label) photo, and adds them to the
 * batch as a 'staged' record (result defaults to Negative, set later in the Testing grid). No result
 * is chosen here. Near-miss + override gating mirror the submit flow.
 */
export function StageCard({
  campaign,
  kit,
  eligibility,
  nearMiss: initialNearMiss,
  config,
  scannedTracking,
  identifiedBy,
  readAs,
  onAdded,
  onCancel,
}: {
  campaign: string;
  kit: MemberKit;
  eligibility: Eligibility;
  nearMiss: NearMiss | null;
  config: IntakeConfig;
  /**
   * The USPS tracking number scanned while identifying this member, when there was one. Sent to the
   * server purely so the cross-client guard can compare the physical piece to the plan being filed;
   * nothing stored on the record is taken from it.
   */
  scannedTracking?: string | null;
  /** How the member was identified, so the server can judge whether a cross-plan near-miss applies. */
  identifiedBy?: 'tracking' | 'typed' | 'queue';
  /**
   * The entered value and the identifier space the server read it in, shown on the confirmation
   * card. Display only: nothing here is sent back or stored on the record.
   */
  readAs?: ReadAs | null;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [nearMiss, setNearMiss] = useState<NearMiss | null>(initialNearMiss);
  const [nearMissAcked, setNearMissAcked] = useState(false);
  // Acknowledged SEPARATELY from the same-campaign near-miss, so dismissing one never dismisses the other.
  const [crossPlanWarning, setCrossPlanWarning] = useState<string | null>(null);
  const [crossPlanAcked, setCrossPlanAcked] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [consentPhotos, setConsentPhotos] = useState<string[]>([]);
  const [devicePhotos, setDevicePhotos] = useState<string[]>([]);
  const [voiceNotes, setVoiceNotes] = useState<string[]>([]);
  const [collectionDate, setCollectionDate] = useState('');
  const [noConsentForm, setNoConsentForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientRequestId = useRef<string>(crypto.randomUUID());

  // Neither can be resulted at the bench: the card shows why, and the only action is back to scan.
  if (eligibility === 'already-resulted' || eligibility === 'result-conflict') {
    return (
      <div className="li-page">
        <div className="li-narrow">
          <ConfirmationCard kit={kit} eligibility={eligibility} readAs={readAs ?? null} />
          <button type="button" onClick={onCancel} className="btn-bench btn-bench-ghost mt-4 w-full">
            Back to scan
          </button>
        </div>
      </div>
    );
  }

  const overrideNeeded = eligibility === 'override-needed';
  const blockedByNearMiss = !!nearMiss && !nearMissAcked;
  const photoNeeded = config.photoRequired;
  const receivedBasis = overrideActive ? 'override' : 'scan';
  const canAdd =
    !blockedByNearMiss &&
    (!overrideNeeded || (config.overrideAllowed && overrideActive)) &&
    (!photoNeeded || consentPhotos.length > 0) &&
    !saving;

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiPost('/api/intake/stage', {
        campaign,
        clientRequestId: clientRequestId.current,
        kitBarcode: kit.kitBarcode,
        accessionBarcode: kit.accessionBarcode,
        memberId: kit.memberId,
        receivedBasis,
        photos: [...consentPhotos, ...devicePhotos],
        currentPhoto: consentPhotos[0] ?? devicePhotos[0] ?? null,
        voiceNotes,
        collectionDate: collectionDate || null,
        noConsentForm,
        nearMissAcknowledged: nearMissAcked,
        scannedTracking: scannedTracking ?? null,
        identifiedBy: identifiedBy ?? 'typed',
        crossClientNearMissAcknowledged: crossPlanAcked,
      });
      if (res.ok) {
        onAdded();
        return;
      }
      if (res.data.code === 'cross-client-near-miss') {
        setCrossPlanWarning((res.data.error as string) || 'This barcode is one digit away from a kit in another health plan.');
        setCrossPlanAcked(false);
        return;
      }
      if (res.data.code === 'near-miss') {
        if (res.data.nearMiss) setNearMiss(res.data.nearMiss as NearMiss);
        setNearMissAcked(false);
        return;
      }
      setError((res.data.error as string) || `Could not add (${res.status})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add to batch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="li-page">
      <div className="li-narrow">
        {nearMiss && !nearMissAcked && (
          <NearMissDialog nearMiss={nearMiss} onAcknowledge={() => setNearMissAcked(true)} onCancel={onCancel} />
        )}

        {/*
          CROSS-PLAN near-miss. Separate from the dialog above on purpose: that one warns about a
          look-alike kit in THIS plan, this one about a look-alike in ANOTHER plan, and acknowledging
          either must never silence the other.
        */}
        {crossPlanWarning && !crossPlanAcked && (
          <div className="note warn mt-3">
            <p className="font-medium">Check this barcode against the kit</p>
            <p className="mt-1">{crossPlanWarning}</p>
            <button
              type="button"
              onClick={() => setCrossPlanAcked(true)}
              className="btn btn-ghost btn-sm mt-2"
            >
              I have checked the number
            </button>
          </div>
        )}

        <div className="li-head">
          <div className="section-eyebrow">Resulting - add to batch</div>
          <h2 className="section-title">Confirm <em>member</em></h2>
        </div>

        <ConfirmationCard kit={kit} eligibility={eligibility} readAs={readAs ?? null} />

        {overrideNeeded && (
          <div className="mt-3">
            <OverrideHatch allowed={config.overrideAllowed} active={overrideActive} onToggle={setOverrideActive} />
          </div>
        )}

        <div className="mt-4 space-y-4">
          <div>
            <label className="field-label" htmlFor="collection-date">
              Collection date (optional, from the card)
            </label>
            <input
              id="collection-date"
              type="date"
              value={collectionDate}
              onChange={(e) => setCollectionDate(e.target.value)}
              className="text-input"
            />
          </div>

          <label className="note warn flex cursor-pointer items-start gap-3" style={{ display: 'flex' }}>
            <input
              type="checkbox"
              checked={noConsentForm}
              onChange={(e) => setNoConsentForm(e.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 accent-[color:var(--amber)]"
            />
            <span>
              <span className="block font-semibold" style={{ color: 'var(--amber)' }}>
                No consent form on file
              </span>
              <span className="mt-0.5 block text-xs" style={{ color: 'var(--amber)' }}>
                Result and letter are still sent. Missing consent only opens background consent research -
                Member Support follows up to get the form on file.
              </span>
            </span>
          </label>

          <PhotoCapture
            campaign={campaign}
            kitBarcode={kit.kitBarcode}
            required={photoNeeded}
            kind="consent"
            label="1. Consent card (or box + label) photo"
            onChange={(p) => setConsentPhotos(p)}
          />
          <PhotoCapture
            campaign={campaign}
            kitBarcode={kit.kitBarcode}
            required={false}
            kind="device"
            label="2. Test device photo"
            onChange={(p) => setDevicePhotos(p)}
          />
          <VoiceNote campaign={campaign} kitBarcode={kit.kitBarcode} onChange={(v) => setVoiceNotes(v)} />
        </div>

        {error && <p className="mt-3 text-sm text-cdx-danger">{error}</p>}

        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onCancel} className="btn-bench btn-bench-ghost flex-1">
            Cancel
          </button>
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => void add()}
            className="btn-bench flex-[2] disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add to batch'}
          </button>
        </div>
      </div>
    </div>
  );
}
